/**
 * AskEvalAgent — a small sparkle button that asks the Evaluation Agent about the
 * part of the page it sits on. The question goes to the eval chat panel through
 * ChatPanelContext.sendPrompt, shows as the user's message and is sent at once;
 * EvalLayout expands the panel when a prompt arrives.
 *
 * The question carries the ids (run, session, evaluator, trace) as plain text, so the
 * agent can look the data up and the user sees exactly what was asked.
 *
 * Renders nothing where EvalLayout shows no chat (chat off, or compact viewports),
 * so the button never sends into a panel that is not there.
 */
import { useTranslation } from "react-i18next";
import { useChatPanel } from "../../../context/ChatPanelContext";
import { EVAL_CHAT_ENABLED } from "../../../config";
import { isCompact, useBreakpoint } from "../../../hooks/useBreakpoint";
import "./AskEvalAgent.css";

export default function AskEvalAgent({ question }: { question: string }) {
  const { t } = useTranslation("eval");
  const { sendPrompt } = useChatPanel();
  const compact = isCompact(useBreakpoint());
  if (!EVAL_CHAT_ENABLED || compact) return null;

  const label = t("askAgent.button");
  return (
    <button
      type="button"
      className="eval-ask-agent"
      title={label}
      aria-label={label}
      onClick={(e) => {
        // Rows and headers under the button are clickable (expand a session or an
        // evaluation); asking must not toggle them too.
        e.stopPropagation();
        sendPrompt(question);
      }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M11 3l1.8 5.2a3 3 0 0 0 1.9 1.9L20 12l-5.3 1.8a3 3 0 0 0-1.9 1.9L11 21l-1.8-5.3a3 3 0 0 0-1.9-1.9L2 12l5.3-1.9a3 3 0 0 0 1.9-1.9L11 3z" />
        <path d="M19 1.5l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7.7-2z" />
      </svg>
    </button>
  );
}

/** A value for a question: fixed digits for numbers, "—" when missing. */
export function askValue(value: unknown, digits = 2): string {
  if (value == null || value === "") return "—";
  const n = typeof value === "number" ? value : parseFloat(String(value));
  if (Number.isNaN(n)) return String(value);
  return n.toFixed(digits);
}
